import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { BigButton } from "../design/BigButton";
import { Card } from "../design/BigButton";
import { ASSETS } from "../lib/assets";
import { api } from "../lib/api";
import { OAuthButtons } from "../auth/OAuthButtons";

export function LoginScreen() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const { login, refresh } = useAuth();
  const navigate = useNavigate();

  const finishAuthRedirect = async (opts = {}) => {
    // Shared post-auth routing: check subscription + role, hop into the
    // right shell. `opts.requiresLegalAcceptance` bounces to re-consent.
    if (opts.requiresLegalAcceptance) {
      navigate("/legal/re-consent", { replace: true });
      return;
    }
    try {
      const { data } = await api.get("/auth/me");
      const st = data?.user?.subscriptionStatus;
      const role = data?.user?.role;
      if (st === "premium" || role === "admin" || role === "content_editor") {
        navigate("/profiles", { replace: true });
      } else {
        navigate("/parent/purchase", { replace: true });
      }
    } catch (_e) {
      navigate("/profiles", { replace: true });
    }
  };

  async function submit(e) {
    e.preventDefault();
    setBusy(true);
    const res = await login(email.trim(), password);
    setBusy(false);
    if (res.ok) {
      await finishAuthRedirect();
    } else {
      setError(res.error);
    }
  }

  return (
    <div className="wmleye-app auth-shell" data-testid="login-screen">
      <div className="auth-shell__panel">
        <img src={ASSETS.logo} alt="With My Little EYE" className="auth-shell__logo" />
        <h1>Welcome back</h1>
        <OAuthButtons
          mode="signin"
          onSuccess={async (res) => {
            await refresh();
            await finishAuthRedirect(res);
          }}
          onError={(msg) => setError(msg)}
        />
        <form onSubmit={submit} className="auth-form">
          <label>
            Email
            <input
              type="email"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              data-testid="login-email"
            />
          </label>
          <label>
            Password
            <input
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              data-testid="login-password"
            />
          </label>
          {error && (
            <div className="auth-form__error" data-testid="login-error">
              {error}
            </div>
          )}
          <BigButton type="submit" disabled={busy} data-testid="login-submit">
            {busy ? "Signing in…" : "Sign in"}
          </BigButton>
        </form>
        <p className="auth-form__foot">
          New here? <Link to="/register" data-testid="login-register-link">Create an account</Link>
        </p>
      </div>
    </div>
  );
}

export function RegisterScreen() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [accepted, setAccepted] = useState(false);
  const [legalVersions, setLegalVersions] = useState({ privacy: "", terms: "" });
  const { register, refresh } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/legal/versions");
        setLegalVersions({
          privacy: data.privacyPolicyVersion,
          terms: data.termsOfServiceVersion,
        });
      } catch (_e) { /* legal page still shows the checkbox — server may reject on submit */ }
    })();
  }, []);

  async function submit(e) {
    e.preventDefault();
    if (!accepted) {
      setError("You must accept the Privacy Policy and Terms of Service.");
      return;
    }
    setBusy(true);
    const res = await register(email.trim(), password, name.trim() || null, legalVersions);
    setBusy(false);
    if (res.ok) navigate("/parent/purchase", { replace: true });
    else setError(res.error);
  }

  return (
    <div className="wmleye-app auth-shell" data-testid="register-screen">
      <div className="auth-shell__panel">
        <img src={ASSETS.logo} alt="With My Little EYE" className="auth-shell__logo" />
        <h1>Create your parent account</h1>
        <OAuthButtons
          mode="signup"
          legalVersions={legalVersions}
          acceptedLegal={accepted}
          onSuccess={async (res) => {
            await refresh();
            if (res.requiresLegalAcceptance) {
              navigate("/legal/re-consent", { replace: true });
            } else {
              navigate("/parent/purchase", { replace: true });
            }
          }}
          onError={(msg) => setError(msg)}
        />
        <form onSubmit={submit} className="auth-form">
          <label>
            Your name (optional)
            <input value={name} onChange={(e) => setName(e.target.value)} data-testid="register-name" />
          </label>
          <label>
            Email
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="register-email" />
          </label>
          <label>
            Password (min 8 chars)
            <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={8} data-testid="register-password" />
          </label>
          <label style={{ display: "flex", alignItems: "flex-start", gap: "0.5rem", fontSize: "0.85rem", marginTop: "0.5rem" }}>
            <input type="checkbox" checked={accepted}
                   onChange={(e) => setAccepted(e.target.checked)}
                   data-testid="register-accept-legal"
                   style={{ marginTop: "0.2rem" }} />
            <span>
              I have read and agree to the{" "}
              <Link to="/legal/privacy" target="_blank" rel="noopener" data-testid="register-privacy-link">Privacy Policy</Link>{" "}
              and{" "}
              <Link to="/legal/terms" target="_blank" rel="noopener" data-testid="register-terms-link">Terms of Service</Link>.
            </span>
          </label>
          {error && <div className="auth-form__error" data-testid="register-error">{error}</div>}
          <BigButton type="submit" disabled={busy || !accepted} data-testid="register-submit">
            {busy ? "Creating…" : "Create account"}
          </BigButton>
        </form>
        <p className="auth-form__foot">
          Already have one? <Link to="/login" data-testid="register-signin-link">Sign in</Link>
        </p>
      </div>
    </div>
  );
}
